<template>
  <div class="vrt">
    <div
      ref="viewport"
      class="vrt-viewport"
      :class="{ 'vrt-viewport--virtual': virtualizationActive }"
      :style="viewportStyle"
      @scroll="onScroll"
    >
      <div ref="header" class="vrt-header">
        <slot name="header"></slot>
      </div>
      <div class="vrt-content" :style="contentStyle">
        <div class="vrt-rows" :class="{ 'vrt-rows--virtual': virtualizationActive }" :style="rowsStyle">
          <div
            v-for="item in visibleItems"
            :key="item.key"
            class="vrt-row"
            :data-vrow-key="item.key"
          >
            <slot name="row" :row="item.row" :index="item.index"></slot>
          </div>
        </div>
        <div v-if="rows.length === 0" class="vrt-empty">
          <slot name="empty">No results found...</slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computeVisibleRange } from '@/store/utils/virtualWindow'

// A dependency-free virtualized list. Only the rows currently in view (plus
// overscan) are mounted, so a large import review never mounts thousands of rows
// — nor, crucially, the per-row selects (the Country select alone carries ~240
// options) for off-screen rows. Row and header markup are supplied by the parent
// through scoped slots, so all cell logic and column layout stay in the parent's
// scope. A single scroll container handles both axes with a sticky header, so the
// header stays aligned with the rows even when columns overflow horizontally.
export default {
  name: 'VirtualReviewTable',
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    // Stable per-row key. Kept as a function so height measurements survive sorting
    // and filtering (which reorder rows) instead of being tied to array position.
    rowKey: {
      type: [Function, String],
      required: true,
    },
    estimatedRowHeight: {
      type: Number,
      default: 64,
    },
    maxHeight: {
      type: Number,
      default: 460,
    },
    overscan: {
      type: Number,
      default: 6,
    },
    // Below this row count the list renders in full (no fixed-height scroll region),
    // so small imports look and behave like an ordinary table.
    virtualizeThreshold: {
      type: Number,
      default: 30,
    },
  },
  data() {
    return {
      scrollTop: 0,
      viewportHeight: this.maxHeight,
      headerHeight: 0,
      // rowKey -> measured pixel height. Persists across reorders.
      measuredHeights: Object.create(null),
      resizeObserver: null,
      measureScheduled: false,
    }
  },
  computed: {
    virtualizationActive() {
      return this.rows.length > this.virtualizeThreshold
    },
    keyedRows() {
      return this.rows.map((row, index) => ({
        row,
        index,
        key: this.keyFor(row, index),
      }))
    },
    heights() {
      return this.keyedRows.map((item) => {
        const measured = this.measuredHeights[item.key]
        return measured > 0 ? measured : this.estimatedRowHeight
      })
    },
    range() {
      if (!this.virtualizationActive) {
        return { start: 0, end: this.rows.length, offsetTop: 0, totalHeight: 0 }
      }
      // The sticky header covers the top `headerHeight` px of the viewport, so the
      // rows' visible band is offset by it on both position and available height.
      return computeVisibleRange({
        heights: this.heights,
        scrollTop: Math.max(0, this.scrollTop - this.headerHeight),
        viewportHeight: Math.max(0, this.viewportHeight - this.headerHeight),
        overscan: this.overscan,
      })
    },
    visibleItems() {
      return this.keyedRows.slice(this.range.start, this.range.end)
    },
    viewportStyle() {
      if (!this.virtualizationActive) {
        // Small groups render in full but still need horizontal scroll when the
        // columns are wider than the dialog.
        return { overflowX: 'auto' }
      }
      return { maxHeight: this.maxHeight + 'px', overflow: 'auto' }
    },
    contentStyle() {
      if (!this.virtualizationActive) {
        return {}
      }
      return { height: this.range.totalHeight + 'px' }
    },
    rowsStyle() {
      if (!this.virtualizationActive) {
        return {}
      }
      return { transform: `translateY(${this.range.offsetTop}px)` }
    },
  },
  watch: {
    rows() {
      // A fresh dataset (new upload) or reorder may drop keys that no longer exist;
      // prune stale measurements so they cannot leak into totalHeight.
      this.pruneMeasuredHeights()
      this.$nextTick(this.measureVisibleRows)
    },
  },
  mounted() {
    this.updateViewportHeight()
    this.updateHeaderHeight()
    if (typeof ResizeObserver !== 'undefined' && this.$refs.viewport) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateViewportHeight()
        this.updateHeaderHeight()
      })
      this.resizeObserver.observe(this.$refs.viewport)
    }
    this.$nextTick(this.measureVisibleRows)
  },
  updated() {
    this.scheduleMeasure()
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  },
  methods: {
    keyFor(row, index) {
      if (typeof this.rowKey === 'function') {
        return this.rowKey(row, index)
      }
      const value = row && row[this.rowKey]
      return value === undefined || value === null ? String(index) : String(value)
    },
    onScroll(event) {
      if (!this.virtualizationActive) return
      this.scrollTop = event.target.scrollTop
    },
    updateViewportHeight() {
      const viewport = this.$refs.viewport
      if (viewport && viewport.clientHeight) {
        this.viewportHeight = viewport.clientHeight
      }
    },
    updateHeaderHeight() {
      const header = this.$refs.header
      if (header) {
        this.headerHeight = header.getBoundingClientRect().height || 0
      }
    },
    scheduleMeasure() {
      if (this.measureScheduled || !this.virtualizationActive) return
      this.measureScheduled = true
      this.$nextTick(() => {
        this.measureScheduled = false
        this.measureVisibleRows()
      })
    },
    measureVisibleRows() {
      if (!this.virtualizationActive || !this.$refs.viewport) return
      this.updateHeaderHeight()
      const rowEls = this.$refs.viewport.querySelectorAll('[data-vrow-key]')
      let changed = false
      rowEls.forEach((el) => {
        const key = el.getAttribute('data-vrow-key')
        const height = el.getBoundingClientRect().height
        if (!height) return
        const previous = this.measuredHeights[key]
        // Ignore sub-pixel churn so measurement cannot oscillate forever.
        if (previous === undefined || Math.abs(previous - height) > 0.5) {
          this.$set(this.measuredHeights, key, height)
          changed = true
        }
      })
      if (changed) {
        this.$nextTick(this.updateViewportHeight)
      }
    },
    pruneMeasuredHeights() {
      const liveKeys = new Set(this.keyedRows.map((item) => item.key))
      Object.keys(this.measuredHeights).forEach((key) => {
        if (!liveKeys.has(key)) {
          this.$delete(this.measuredHeights, key)
        }
      })
    },
    // Exposed for the parent so a search/sort can return the scroll to the top.
    scrollToTop() {
      if (this.$refs.viewport) {
        this.$refs.viewport.scrollTop = 0
      }
      this.scrollTop = 0
    },
  },
}
</script>

<style scoped>
.vrt {
  width: 100%;
}

.vrt-viewport {
  position: relative;
  width: 100%;
}

.vrt-viewport--virtual {
  will-change: transform;
}

.vrt-header {
  position: sticky;
  top: 0;
  z-index: 2;
}

.vrt-content {
  position: relative;
}

.vrt-rows--virtual {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

.vrt-empty {
  padding: 48px 20px;
  text-align: center;
  color: #8a8a8a;
  font-size: 13px;
}
</style>
